import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { InputWrapperComponent } from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ApiCisternContentDataResponseDto,
  ApiCisternResponseDto,
  TimeInterval,
} from '@birco/rainmesh-api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  SensorDataFilter,
  SensorDataService,
} from '../../../../core/services/sensor-data.service';
import { Store } from '@ngrx/store';
import {
  selectAllCisterns,
  selectCisternById,
} from '../../../cisterns/state/cistern.selectors';
import { ChartSensorDataComponent } from '../../components/chart-sensor-data/chart-sensor-data.component';
import { ChartWaterLevelComponent } from '../../components/chart-water-level/chart-water-level.component';
import { StatisticsPeriodInputComponent } from '../../components/statistics-period-input/statistics-period-input.component';
import { CisternActions } from '../../../cisterns/state/cistern.actions';
import { debounceTime } from 'rxjs/operators';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { CisternItemCardComponent } from '../../../cisterns/components/cistern-item-card/cistern-item-card.component';
import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs';
import { StatisticsChartTab } from '../../models/statistics-chart-tab.type';
import { StatisticsTableRow } from '../../models/statistics-table-row.interface';
import { WaterLevelChartSeriesPoint } from '../../models/water-level-chart-series-point.interface';
import { LocaleService } from '../../../../translation/services/locale.service';
import { LocalizedNumberPipe } from '../../../../shared/pipes/localized-number.pipe';
import { LocalizedPercentPipe } from '../../../../shared/pipes/localized-percent.pipe';
import { ViewPreferenceService } from '../../../../core/services/view-preference.service';

const DEFAULT_CHART_TAB: StatisticsChartTab = 'delta';
const DEFAULT_VIEW_MODE: StatisticsViewMode = 'single';
const DEFAULT_COMPARE_CHART_MODE: CompareChartMode = 'split';
const VIEW_PREFERENCE_KEY = 'statistics.page';

type StatisticsViewMode = 'single' | 'compare';
type StatisticsPeriodKey = 'primary' | 'secondary';
type CompareChartMode = 'split' | 'overlap';

interface StatisticsDataState {
  filter: SensorDataFilter | null;
  rows: StatisticsTableRow[];
}

interface ComparePanelViewModel {
  filter: SensorDataFilter | null;
  interval: TimeInterval | null;
  rows: StatisticsTableRow[];
  deltaChartData: {
    name: string;
    series: { name: string; timestamp: string; value: number }[];
  }[];
  waterLevelChartData: { name: string; series: WaterLevelChartSeriesPoint[] }[];
}

interface ComparePanelsViewModel {
  primary: ComparePanelViewModel;
  secondary: ComparePanelViewModel;
}

interface ComparePanelsState {
  loading: boolean;
  panels: ComparePanelsViewModel;
}

const EMPTY_COMPARE_PANEL: ComparePanelViewModel = {
  filter: null,
  interval: null,
  rows: [],
  deltaChartData: [],
  waterLevelChartData: [],
};

const EMPTY_COMPARE_PANELS: ComparePanelsViewModel = {
  primary: EMPTY_COMPARE_PANEL,
  secondary: EMPTY_COMPARE_PANEL,
};

@Component({
  selector: 'app-page-statistics',
  imports: [
    CommonModule,
    CardComponent,
    InputWrapperComponent,
    ReactiveFormsModule,
    TranslatePipe,
    ChartSensorDataComponent,
    ChartWaterLevelComponent,
    StatisticsPeriodInputComponent,
    IconComponent,
    CisternItemCardComponent,
    MatTabGroup,
    MatTab,
    MatTabContent,
    LocalizedNumberPipe,
    LocalizedPercentPipe,
  ],
  templateUrl: './page-statistics.component.html',
  styleUrl: './page-statistics.component.scss',
})
export class PageStatisticsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private _fb = inject(FormBuilder);
  private _translateService = inject(TranslateService);
  private _sensorDataService = inject(SensorDataService);
  private store = inject(Store);
  private localeService = inject(LocaleService);
  private readonly viewPreferenceService = inject(ViewPreferenceService);

  private destroyRef = inject(DestroyRef);
  private today = new Date();

  loading$ = this._sensorDataService.getLoading$();
  pageLoading$: Observable<boolean>;
  cisternOptions$ = this.store.select(selectAllCisterns).pipe(
    map((cisterns) =>
      cisterns.map(
        (cistern) =>
          ({
            label: cistern.name,
            value: cistern.id,
          }) as { label: string; value: string },
      ),
    ),
    startWith([] as { label: string; value: string }[]),
  );
  sensorData$ = this._sensorDataService.getData$().pipe(
    distinctUntilChanged(
      (a, b) =>
        JSON.stringify(a.data) === JSON.stringify(b.data) &&
        JSON.stringify(a.filter) === JSON.stringify(b.filter),
    ),
    map(({ data, filter }) => this.prepareDataForChart(data, filter) ?? []),
  );
  chartInterval$ = this._sensorDataService.getData$().pipe(
    map(({ filter }) => filter.interval),
    distinctUntilChanged(),
  );
  deltaChartData$ = this.sensorData$.pipe(
    map((data) => this.toDeltaChartData(data)),
  );
  waterLevelChartData$ = this.sensorData$.pipe(
    map((data) => this.toWaterLevelChartData(data)),
  );

  hasDeltaData$ = this.sensorData$.pipe(
    map((data) => this.hasDeltaData(data)),
    distinctUntilChanged(),
  );
  hasWaterLevelData$ = this.sensorData$.pipe(
    map((data) => this.hasWaterLevelData(data)),
    distinctUntilChanged(),
  );

  periodOptions = [
    { labelKey: 'statistics.period.yearly', value: 'yearly' },
    { labelKey: 'statistics.period.monthly', value: 'monthly' },
    { labelKey: 'statistics.period.daily', value: 'daily' },
  ];
  viewModeOptions: { labelKey: string; value: StatisticsViewMode }[] = [
    { labelKey: 'statistics.viewMode.single', value: 'single' },
    { labelKey: 'statistics.viewMode.compare', value: 'compare' },
  ];
  compareChartModeOptions: { labelKey: string; value: CompareChartMode }[] = [
    { labelKey: 'statistics.compare.chartMode.split', value: 'split' },
    { labelKey: 'statistics.compare.chartMode.overlap', value: 'overlap' },
  ];

  isChartView = true;
  selectedChartTab: StatisticsChartTab = DEFAULT_CHART_TAB;
  statisticsForm: FormGroup;
  cistern$: Observable<ApiCisternResponseDto | null>;
  selectedCisternId$: Observable<string | null>;
  viewMode$: Observable<StatisticsViewMode>;
  compareChartMode$: Observable<CompareChartMode>;
  primaryFilter$: Observable<SensorDataFilter>;
  compareFilters$: Observable<{
    primary: SensorDataFilter | null;
    secondary: SensorDataFilter | null;
  }>;
  compareLoading$: Observable<boolean>;
  comparePanels$: Observable<ComparePanelsViewModel>;
  compareOverlapDeltaChartData$: Observable<
    {
      name: string;
      series: { name: string; timestamp: string; value: number }[];
    }[]
  >;
  compareOverlapWaterLevelChartData$: Observable<
    { name: string; series: WaterLevelChartSeriesPoint[] }[]
  >;
  compareOverlapInterval$: Observable<TimeInterval | null>;
  hasCompareOverlapDeltaData$: Observable<boolean>;
  hasCompareOverlapWaterLevelData$: Observable<boolean>;

  constructor() {
    this.selectedChartTab = this.resolveInitialChartTab(
      this.route.snapshot.queryParamMap.get('chartTab'),
    );
    this.statisticsForm = this._fb.group({
      viewMode: [DEFAULT_VIEW_MODE, Validators.required],
      compareChartMode: [DEFAULT_COMPARE_CHART_MODE, Validators.required],
      cistern: [null],
      period: ['monthly', Validators.required],
      primary: this.createPeriodForm(this.today),
      secondary: this.createPeriodForm(this.getComparisonDefaultDate()),
    });

    this.viewMode$ = this.statisticsForm
      .get('viewMode')!
      .valueChanges.pipe(
        startWith(
          this.statisticsForm.get('viewMode')!.value as StatisticsViewMode,
        ),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this.selectedCisternId$ = this.statisticsForm
      .get('cistern')!
      .valueChanges.pipe(
        startWith(this.statisticsForm.get('cistern')!.value as string | null),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this.compareChartMode$ = this.statisticsForm
      .get('compareChartMode')!
      .valueChanges.pipe(
        startWith(
          this.statisticsForm.get('compareChartMode')!
            .value as CompareChartMode,
        ),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    this.viewMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncCompareChartModeControlState());

    this.primaryFilter$ = this.createFilterStream('primary');
    this.compareFilters$ = this.createCompareFilterStream();
    const compareState$ = combineLatest([
      this.viewMode$,
      this.compareFilters$,
    ]).pipe(
      filter(([viewMode]) => viewMode === 'compare'),
      switchMap(([, filters]) => {
        if (!filters.primary || !filters.secondary) {
          return of({
            loading: false,
            panels: EMPTY_COMPARE_PANELS,
          });
        }

        return this.fetchComparePanels(filters.primary, filters.secondary).pipe(
          map((panels) => ({
            loading: false,
            panels,
          })),
          startWith({
            loading: true,
            panels: EMPTY_COMPARE_PANELS,
          }),
        );
      }),
      startWith({
        loading: false,
        panels: EMPTY_COMPARE_PANELS,
      } satisfies ComparePanelsState),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.compareLoading$ = compareState$.pipe(
      map((state) => state.loading),
      distinctUntilChanged(),
    );
    this.comparePanels$ = compareState$.pipe(map((state) => state.panels));
    this.compareOverlapDeltaChartData$ = this.comparePanels$.pipe(
      map((panels) =>
        this.toOverlapDeltaChartData(panels.primary, panels.secondary),
      ),
    );
    this.compareOverlapWaterLevelChartData$ = this.comparePanels$.pipe(
      map((panels) =>
        this.toOverlapWaterLevelChartData(panels.primary, panels.secondary),
      ),
    );
    this.compareOverlapInterval$ = this.comparePanels$.pipe(
      map((panels) =>
        panels.primary.interval === panels.secondary.interval
          ? panels.primary.interval
          : null,
      ),
      distinctUntilChanged(),
    );
    this.hasCompareOverlapDeltaData$ = this.comparePanels$.pipe(
      map(
        (panels) =>
          this.hasDeltaData(panels.primary.rows) ||
          this.hasDeltaData(panels.secondary.rows),
      ),
      distinctUntilChanged(),
    );
    this.hasCompareOverlapWaterLevelData$ = this.comparePanels$.pipe(
      map(
        (panels) =>
          this.hasWaterLevelData(panels.primary.rows) ||
          this.hasWaterLevelData(panels.secondary.rows),
      ),
      distinctUntilChanged(),
    );
    this.pageLoading$ = combineLatest([
      this.viewMode$,
      this.loading$,
      this.compareLoading$,
    ]).pipe(
      map(([viewMode, loading, compareLoading]) =>
        viewMode === 'compare' ? compareLoading : loading,
      ),
      distinctUntilChanged(),
    );
    this.isChartView = this.viewPreferenceService.getIsListView(
      `${VIEW_PREFERENCE_KEY}.contentMode`,
      true,
    );

    this.cistern$ = defer(() => {
      const cisternControl = this.statisticsForm.get('cistern');
      const activeSensorData$ = combineLatest([
        this.viewMode$,
        this.sensorData$,
        this.comparePanels$,
      ]).pipe(
        map(([viewMode, singleSensorData, comparePanels]) =>
          viewMode === 'compare'
            ? comparePanels.primary.rows
            : singleSensorData,
        ),
      );

      return combineLatest([
        cisternControl?.valueChanges.pipe(
          startWith(cisternControl.value),
          switchMap((cisternId: string | null) =>
            cisternId
              ? this.store.select(selectCisternById(cisternId))
              : of(null),
          ),
        ) ?? of(null),
        activeSensorData$,
      ]).pipe(
        map(([cistern, sensorData]) =>
          this.withStatisticsFallback(cistern, sensorData),
        ),
      );
    });
  }

  get selectedChartTitleKey(): string {
    return this.selectedChartTab === 'level'
      ? 'statistics.chartTabs.level'
      : 'statistics.chartTabs.delta';
  }

  get isCompareMode(): boolean {
    return this.statisticsForm.get('viewMode')?.value === 'compare';
  }

  get isCompareOverlapMode(): boolean {
    return this.statisticsForm.get('compareChartMode')?.value === 'overlap';
  }

  getPeriodTitleKey(periodKey: StatisticsPeriodKey): string {
    if (periodKey === 'primary') {
      return this.isCompareMode
        ? 'statistics.compare.periodA'
        : 'statistics.period.label';
    }

    return 'statistics.compare.periodB';
  }

  get primaryPeriodForm(): FormGroup {
    return this.statisticsForm.get('primary') as FormGroup;
  }

  get secondaryPeriodForm(): FormGroup {
    return this.statisticsForm.get('secondary') as FormGroup;
  }

  ngOnInit() {
    this.store.dispatch(CisternActions.public.loadAllCisternsByFilters({}));
    this.initFilterSubscription();
    this.initRouteSubscription();
    this.initCompareSubscription();
  }

  onChartTabChange(index: number) {
    this.selectedChartTab = index === 0 ? 'level' : 'delta';
    this.viewPreferenceService.setIsListView(
      `${VIEW_PREFERENCE_KEY}.chartTab`,
      this.selectedChartTab === 'delta',
    );
  }

  onChartTabChangeLevelFirst(index: number) {
    this.selectedChartTab = index === 0 ? 'level' : 'delta';
    this.viewPreferenceService.setIsListView(
      `${VIEW_PREFERENCE_KEY}.chartTab`,
      this.selectedChartTab === 'delta',
    );
  }

  toggleChartView() {
    this.isChartView = !this.isChartView;
    this.viewPreferenceService.setIsListView(
      `${VIEW_PREFERENCE_KEY}.contentMode`,
      this.isChartView,
    );
    this.syncCompareChartModeControlState();
  }

  convertFilter(
    cistern: string | null,
    period: string | null,
    form: FormGroup,
  ): SensorDataFilter | null {
    const selectedDate = form.get('date')?.value as Date | null;

    if (!cistern || !period || !selectedDate) {
      console.warn('Fehlende Pflichtwerte im Filter:', {
        cistern,
        period,
        selectedDate,
      });
      return null;
    }

    let startDate: Date;
    let interval: TimeInterval;

    switch (period) {
      case 'daily':
        startDate = new Date(selectedDate);
        interval = TimeInterval.HOURLY;
        break;

      case 'monthly':
        startDate = new Date(
          Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
        );
        interval = TimeInterval.DAILY;
        break;

      case 'yearly':
        startDate = new Date(Date.UTC(selectedDate.getFullYear(), 0, 1));
        interval = TimeInterval.MONTHLY;
        break;

      default:
        console.warn('Unbekannte Period:', period);
        return null;
    }

    return {
      cisternId: cistern,
      startDate: startDate.toISOString(),
      interval,
    };
  }

  private initFilterSubscription() {
    combineLatest([this.viewMode$, this.primaryFilter$])
      .pipe(
        filter(([viewMode]) => viewMode === 'single'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([, filter]) => this._sensorDataService.changeFilter(filter));
  }

  private initRouteSubscription() {
    const cisternControl = this.statisticsForm.get('cistern');

    combineLatest([
      this.cisternOptions$,
      this.route.queryParamMap.pipe(
        map((params) => ({
          cisternId: params.get('cisternId'),
          chartTab: this.resolveInitialChartTab(params.get('chartTab')),
        })),
      ),
    ])
      .pipe(
        filter(([options]) => options.length > 0),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([options, routeState]) => {
        const currentCisternId = cisternControl?.value as string | null;
        const nextCisternId = this.resolveSelectedCisternId(
          currentCisternId,
          routeState.cisternId,
          options,
        );

        this.selectedChartTab = routeState.chartTab;
        this.viewPreferenceService.setIsListView(
          `${VIEW_PREFERENCE_KEY}.chartTab`,
          this.selectedChartTab === 'delta',
        );

        if (nextCisternId && currentCisternId !== nextCisternId) {
          cisternControl?.patchValue(nextCisternId);
        }
      });
  }

  private initCompareSubscription() {
    this.comparePanels$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private prepareDataForChart(
    data: ApiCisternContentDataResponseDto | null,
    filter: { interval: TimeInterval } | null,
  ): StatisticsTableRow[] | null {
    if (!data || !filter) {
      return null;
    }

    const positives = data.positiveDeltasForInterval ?? {};
    const negatives = data.negativeDeltasForInterval ?? {};
    const waterLevels = data.waterLevel ?? {};
    const waterVolumes = data.waterVolume ?? {};

    const isoDates = Array.from(
      new Set([
        ...Object.keys(positives),
        ...Object.keys(negatives),
        ...Object.keys(waterLevels),
        ...Object.keys(waterVolumes),
      ]),
    ).sort();

    if (isoDates.length === 0) {
      return [];
    }

    return isoDates.map((isoDate) => {
      const negative = this.toChartValue(this.toAbsolute(negatives[isoDate]));

      return {
        name: this.formatDateLabel(new Date(isoDate), filter.interval),
        timestamp: isoDate,
        series: [
          {
            name: this._translateService.instant('statistics.negative'),
            timestamp: isoDate,
            value: negative,
            unit: 'l',
          },
          {
            name: this._translateService.instant('statistics.positive'),
            timestamp: isoDate,
            value: this.toChartValue(positives[isoDate]),
            unit: 'l',
          },
          {
            name: this._translateService.instant('statistics.waterLevel'),
            timestamp: isoDate,
            value: this.toChartValue(waterLevels[isoDate]),
            unit: 'l',
          },
          {
            name: this._translateService.instant('statistics.waterVolume'),
            timestamp: isoDate,
            value: this.toChartValue(waterVolumes[isoDate]),
            unit: '%',
          },
        ],
      };
    });
  }

  private formatDateLabel(date: Date, interval: TimeInterval): string {
    const locale = this.localeService.userLanguage;

    switch (interval) {
      case TimeInterval.MONTHLY:
        return date.toLocaleDateString(locale, { month: 'short' });
      case TimeInterval.DAILY:
        return date.toLocaleDateString(locale, { day: 'numeric' });
      case TimeInterval.HOURLY:
      default:
        return date.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
        });
    }
  }

  private toNonNegativeNumber(value: unknown): number {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) && numericValue >= 0
      ? numericValue
      : 0;
  }

  /**
   * Converts a raw reading to a chart value while preserving missing readings as `null`.
   * `null`/`undefined`/non-finite values stay `null` so that date entries are still displayed
   * without fabricating a `0`. Finite negatives are clamped to `0`.
   */
  /**
   * Returns the absolute value of a reading while keeping missing readings (`null`/`undefined`/
   * non-finite) as-is so that {@link toChartValue} can preserve them as `null`.
   */
  private toAbsolute(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }

    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? Math.abs(numericValue) : value;
  }

  private toChartValue(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return null;
    }

    return numericValue >= 0 ? numericValue : 0;
  }

  private getSeriesValue(
    row: StatisticsTableRow,
    index: number,
  ): number | null {
    return row.series[index]?.value ?? null;
  }

  private hasNonNullSeriesValue(
    rows: StatisticsTableRow[],
    seriesIndices: number[],
  ): boolean {
    return rows.some((row) =>
      seriesIndices.some((index) => this.getSeriesValue(row, index) !== null),
    );
  }

  /** Delta charts only carry meaningful data when at least one negative/positive reading is non-null. */
  hasDeltaData(rows: StatisticsTableRow[]): boolean {
    return this.hasNonNullSeriesValue(rows, [0, 1]);
  }

  /** Water level charts only carry meaningful data when at least one water level/volume reading is non-null. */
  hasWaterLevelData(rows: StatisticsTableRow[]): boolean {
    return this.hasNonNullSeriesValue(rows, [2, 3]);
  }

  private toDeltaChartData(data: StatisticsTableRow[]): {
    name: string;
    series: { name: string; timestamp: string; value: number }[];
  }[] {
    return data.map((row) => ({
      name: row.name,
      series: row.series.slice(0, 2).map((series) => ({
        name: series.name,
        timestamp: series.timestamp,
        value: series.value ?? 0,
      })),
    }));
  }

  private toWaterLevelChartData(
    data: StatisticsTableRow[],
  ): { name: string; series: WaterLevelChartSeriesPoint[] }[] {
    return data.length === 0
      ? []
      : [
          {
            name: this._translateService.instant('statistics.chartTabs.level'),
            series: data.map((row) => ({
              name: row.name,
              timestamp: row.timestamp,
              value: this.getSeriesValue(row, 3),
              absoluteLiters: this.getSeriesValue(row, 2),
            })),
          },
        ];
  }

  private toComparePanelViewModel(
    state: StatisticsDataState,
  ): ComparePanelViewModel {
    return {
      filter: state.filter,
      interval: state.filter?.interval ?? null,
      rows: state.rows,
      deltaChartData: this.toDeltaChartData(state.rows),
      waterLevelChartData: this.toWaterLevelChartData(state.rows),
    };
  }

  private fetchComparePanels(
    primaryFilter: SensorDataFilter,
    secondaryFilter: SensorDataFilter,
  ): Observable<ComparePanelsViewModel> {
    return forkJoin({
      primary: this._sensorDataService.getDataForFilter(primaryFilter),
      secondary: this._sensorDataService.getDataForFilter(secondaryFilter),
    }).pipe(
      map(({ primary, secondary }) => ({
        primary: this.toComparePanelViewModel({
          filter: primary.filter,
          rows: this.prepareDataForChart(primary.data, primary.filter) ?? [],
        }),
        secondary: this.toComparePanelViewModel({
          filter: secondary.filter,
          rows:
            this.prepareDataForChart(secondary.data, secondary.filter) ?? [],
        }),
      })),
    );
  }

  private toOverlapDeltaChartData(
    primary: ComparePanelViewModel,
    secondary: ComparePanelViewModel,
  ): {
    name: string;
    series: { name: string; timestamp: string; value: number }[];
  }[] {
    const primaryLabel =
      this.formatFilterSummary(primary.filter) ||
      this._translateService.instant('statistics.compare.periodA');
    const secondaryLabel =
      this.formatFilterSummary(secondary.filter) ||
      this._translateService.instant('statistics.compare.periodB');
    const rowMap = new Map<
      string,
      { primary?: StatisticsTableRow; secondary?: StatisticsTableRow }
    >();

    for (const row of primary.rows) {
      rowMap.set(row.name, { ...rowMap.get(row.name), primary: row });
    }

    for (const row of secondary.rows) {
      rowMap.set(row.name, { ...rowMap.get(row.name), secondary: row });
    }

    return Array.from(rowMap.entries())
      .map(([name, rows]) => ({
        name,
        series: [
          this.toOverlapDeltaSeriesPoint(primaryLabel, rows.primary?.series[0]),
          this.toOverlapDeltaSeriesPoint(primaryLabel, rows.primary?.series[1]),
          this.toOverlapDeltaSeriesPoint(
            secondaryLabel,
            rows.secondary?.series[0],
          ),
          this.toOverlapDeltaSeriesPoint(
            secondaryLabel,
            rows.secondary?.series[1],
          ),
        ].filter(
          (
            seriesPoint,
          ): seriesPoint is {
            name: string;
            timestamp: string;
            value: number;
          } => !!seriesPoint,
        ),
      }))
      .filter((row) => row.series.length > 0);
  }

  private toOverlapWaterLevelChartData(
    primary: ComparePanelViewModel,
    secondary: ComparePanelViewModel,
  ): { name: string; series: WaterLevelChartSeriesPoint[] }[] {
    const primaryLabel =
      this.formatFilterSummary(primary.filter) ||
      this._translateService.instant('statistics.compare.periodA');
    const secondaryLabel =
      this.formatFilterSummary(secondary.filter) ||
      this._translateService.instant('statistics.compare.periodB');

    return [
      {
        name: primaryLabel,
        series: primary.rows.map((row) => ({
          name: row.name,
          timestamp: row.timestamp,
          seriesLabel: primaryLabel,
          value: this.getSeriesValue(row, 3),
          absoluteLiters: this.getSeriesValue(row, 2),
        })),
      },
      {
        name: secondaryLabel,
        series: secondary.rows.map((row) => ({
          name: row.name,
          timestamp: row.timestamp,
          seriesLabel: secondaryLabel,
          value: this.getSeriesValue(row, 3),
          absoluteLiters: this.getSeriesValue(row, 2),
        })),
      },
    ].filter((series) => series.series.length > 0);
  }

  private toOverlapDeltaSeriesPoint(
    periodLabel: string,
    series: StatisticsTableRow['series'][number] | undefined,
  ): { name: string; timestamp: string; value: number } | null {
    if (!series) {
      return null;
    }

    return {
      name: `${periodLabel} - ${series.name}`,
      timestamp: series.timestamp,
      value: series.value ?? 0,
    };
  }

  private withStatisticsFallback(
    cistern: ApiCisternResponseDto | null,
    sensorData: StatisticsTableRow[],
  ): ApiCisternResponseDto | null {
    if (!cistern) {
      return null;
    }

    const latestPoint = sensorData.at(-1);

    if (!latestPoint) {
      return cistern;
    }

    const currentWaterVolume = this.toNonNegativeNumber(
      cistern.currentWaterVolume,
    );
    const currentWaterPercentage = this.toNonNegativeNumber(
      cistern.currentWaterPercentage,
    );

    if (currentWaterVolume > 0 || currentWaterPercentage > 0) {
      return cistern;
    }

    const waterLevel = this.getSeriesValue(latestPoint, 2) ?? 0;
    const waterVolume = this.getSeriesValue(latestPoint, 3) ?? 0;

    if (waterLevel <= 0 && waterVolume <= 0) {
      return cistern;
    }

    return {
      ...cistern,
      currentWaterVolume: waterLevel,
      currentWaterPercentage: waterVolume,
    };
  }

  private resolveSelectedCisternId(
    currentCisternId: string | null,
    requestedCisternId: string | null,
    options: { label: string; value: string }[],
  ): string | null {
    if (
      requestedCisternId &&
      options.some((option) => option.value === requestedCisternId)
    ) {
      return requestedCisternId;
    }

    if (
      currentCisternId &&
      options.some((option) => option.value === currentCisternId)
    ) {
      return currentCisternId;
    }

    return options[0]?.value ?? null;
  }

  private toChartTab(chartTab: string | null): StatisticsChartTab {
    return chartTab === 'level' ? 'level' : DEFAULT_CHART_TAB;
  }

  private resolveInitialChartTab(
    routeChartTab: string | null,
  ): StatisticsChartTab {
    if (routeChartTab) {
      return this.toChartTab(routeChartTab);
    }

    return this.viewPreferenceService.getIsListView(
      `${VIEW_PREFERENCE_KEY}.chartTab`,
      true,
    )
      ? 'delta'
      : 'level';
  }

  private createPeriodForm(date: Date): FormGroup {
    return this._fb.group({
      date: [date, Validators.required],
    });
  }

  private getComparisonDefaultDate(): Date {
    return new Date(this.today.getFullYear(), this.today.getMonth() - 1, 1);
  }

  private createFilterStream(
    periodKey: 'primary' | 'secondary',
  ): Observable<SensorDataFilter> {
    const periodForm = this.getPeriodForm(periodKey);

    return combineLatest([
      this.selectedCisternId$,
      this.statisticsForm
        .get('period')!
        .valueChanges.pipe(startWith(this.statisticsForm.get('period')!.value)),
      periodForm.valueChanges.pipe(startWith(periodForm.getRawValue())),
    ]).pipe(
      debounceTime(300),
      filter(() => periodForm.valid),
      map(([cistern, period]) =>
        this.convertFilter(cistern, period, periodForm),
      ),
      filter(
        (statisticsFilter): statisticsFilter is SensorDataFilter =>
          !!statisticsFilter,
      ),
      distinctUntilChanged((a, b) => {
        return (
          a.cisternId === b.cisternId &&
          a.startDate === b.startDate &&
          a.interval === b.interval
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private createCompareFilterStream(): Observable<{
    primary: SensorDataFilter | null;
    secondary: SensorDataFilter | null;
  }> {
    return combineLatest([
      this.selectedCisternId$,
      this.statisticsForm
        .get('period')!
        .valueChanges.pipe(startWith(this.statisticsForm.get('period')!.value)),
      this.primaryPeriodForm.valueChanges.pipe(
        startWith(this.primaryPeriodForm.getRawValue()),
      ),
      this.secondaryPeriodForm.valueChanges.pipe(
        startWith(this.secondaryPeriodForm.getRawValue()),
      ),
    ]).pipe(
      debounceTime(300),
      map(([cistern, period]) => ({
        primary: this.convertFilter(cistern, period, this.primaryPeriodForm),
        secondary: this.convertFilter(
          cistern,
          period,
          this.secondaryPeriodForm,
        ),
      })),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getPeriodForm(periodKey: StatisticsPeriodKey): FormGroup {
    return this.statisticsForm.get(periodKey) as FormGroup;
  }

  private syncCompareChartModeControlState() {
    const compareChartModeControl = this.statisticsForm.get('compareChartMode');

    if (!compareChartModeControl) {
      return;
    }

    if (this.isChartView && this.isCompareMode) {
      compareChartModeControl.enable({ emitEvent: false });
      return;
    }

    compareChartModeControl.disable({ emitEvent: false });
  }

  formatFilterSummary(filter: SensorDataFilter | null): string {
    if (!filter) {
      return '';
    }

    const startDate = new Date(filter.startDate);
    const locale = this.localeService.userLanguage;

    switch (filter.interval) {
      case TimeInterval.MONTHLY:
        return startDate.toLocaleDateString(locale, { year: 'numeric' });
      case TimeInterval.DAILY:
        return startDate.toLocaleDateString(locale, {
          month: 'long',
          year: 'numeric',
        });
      case TimeInterval.HOURLY:
      default:
        return startDate.toLocaleDateString(locale, {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        });
    }
  }
}
